import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_LAYOUT_FOLDER = "startalignllc_data/plant1/factory_layout";
const DEFAULT_LAYOUT_FILE = "factory_layout_standards.json";
const REFRESH_MS = 60000;
const DEFAULT_HOURLY_FOLDER = "startalignllc_data/plant1/process_hourly_data/raw";
const DEFAULT_DAILY_FOLDER = "startalignllc_data/plant1/process_daily_data/raw";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const byProcessOrder = (a, b) => {
  const aOrder = Number(a?.process_order || 0);
  const bOrder = Number(b?.process_order || 0);
  if (aOrder !== bOrder) return aOrder - bOrder;
  return Number(a?.process_id || 0) - Number(b?.process_id || 0);
};

const getNumeric = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const normalizeMachineLabel = (value) => String(value || "").trim().toLowerCase();

const getLastSeriesMachine = (process) => {
  const machines = Array.isArray(process?.machines) ? process.machines : [];
  if (!machines.length) return null;

  const sorted = [...machines].sort((a, b) => {
    const aSeq = Number(a?.sequence_order || 0);
    const bSeq = Number(b?.sequence_order || 0);
    if (aSeq !== bSeq) return aSeq - bSeq;
    return String(a?.machine_id || a?.machine_name || "").localeCompare(
      String(b?.machine_id || b?.machine_name || ""),
    );
  });

  const last = sorted[sorted.length - 1];
  return {
    id: normalizeMachineLabel(last?.machine_id),
    name: normalizeMachineLabel(last?.machine_name),
    standard: getNumeric(last?.standard_pieces_per_hour),
  };
};

const parseIsoDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = raw.replace(/\//g, "-");

  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(normalized)) {
    const [y, m, d] = normalized.slice(0, 10).split("-");
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(normalized)) {
    const [m, d, y] = normalized.split("-");
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  return null;
};

const averageOf = (values) => {
  const valid = values.filter((value) => Number.isFinite(value));
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const normalizeDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return { start: "", end: "" };
  const start = startDate || endDate || "";
  const end = endDate || startDate || "";
  if (start && end && start > end) return { start: end, end: start };
  return { start, end };
};

const isDateInRange = (date, startDate, endDate) => {
  if (!date) return false;
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
};

const formatDisplayDate = (isoDate) => {
  const value = String(isoDate || "").trim();
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${month}/${day}/${year}`;
};

const formatDisplayDateWords = (isoDate) => {
  const value = String(isoDate || "").trim();
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const monthIndex = Number(month) - 1;
  const monthLabel = monthNames[monthIndex] || month;
  return `${Number(day)} ${monthLabel} ${year}`;
};

const YESTERDAY_ALIAS_DATE = "2025-06-30";

const computeGoalPph = (process) => {
  const machines = Array.isArray(process?.machines) ? process.machines : [];
  const standards = machines
    .map((machine) => getNumeric(machine?.standard_pieces_per_hour))
    .filter((value) => !Number.isNaN(value));
  if (!standards.length) return null;

  const config = String(process?.machine_configuration || "").toUpperCase();
  if (config === "PARALLEL") return standards.reduce((sum, value) => sum + value, 0);
  if (config === "SERIES") {
    const lastMachine = getLastSeriesMachine(process);
    return Number.isFinite(lastMachine?.standard) ? lastMachine.standard : standards[standards.length - 1];
  }
  return standards.reduce((sum, value) => sum + value, 0);
};

const computeActualPph = (process, rows, targetDate, prefilteredRows = null) => {
  if (!Array.isArray(rows) || !rows.length || !targetDate) return null;

  const rowsOnDate = Array.isArray(prefilteredRows)
    ? prefilteredRows
    : rows.filter((row) => parseIsoDate(row?.date) === targetDate);
  if (!rowsOnDate.length) return null;

  const lastSeriesMachine = getLastSeriesMachine(process);
  const config = String(process?.machine_configuration || "").toUpperCase();

  const buckets = rowsOnDate.reduce((acc, row) => {
    const hourKey = String(row?.hour_start || row?.hour_end || "hour").trim() || "hour";
    if (!acc[hourKey]) acc[hourKey] = [];
    acc[hourKey].push(row);
    return acc;
  }, {});

  const perHourValues = Object.values(buckets)
    .map((hourRows) => {
      const values = hourRows.map((row) => getNumeric(row?.actual_units)).filter((v) => !Number.isNaN(v));
      if (!values.length) return NaN;

      if (config === "PARALLEL") return values.reduce((sum, value) => sum + value, 0);
      if (config === "SERIES") {
        const targetRow = hourRows.find((row) => {
          const machine = normalizeMachineLabel(row?.machine);
          return machine && (machine === lastSeriesMachine?.id || machine === lastSeriesMachine?.name);
        });
        const targetValue = getNumeric(targetRow?.actual_units);
        if (Number.isFinite(targetValue)) return targetValue;
        return values[values.length - 1];
      }
      return values.reduce((sum, value) => sum + value, 0);
    })
    .filter((value) => Number.isFinite(value));

  if (!perHourValues.length) return null;
  return perHourValues.reduce((sum, value) => sum + value, 0) / perHourValues.length;
};

const computePlannedPph = (process, rows, targetDate, prefilteredRows = null) => {
  if (!Array.isArray(rows) || !rows.length || !targetDate) return null;

  const rowsOnDate = Array.isArray(prefilteredRows)
    ? prefilteredRows
    : rows.filter((row) => parseIsoDate(row?.date) === targetDate);
  if (!rowsOnDate.length) return null;

  const lastSeriesMachine = getLastSeriesMachine(process);
  const config = String(process?.machine_configuration || "").toUpperCase();

  const buckets = rowsOnDate.reduce((acc, row) => {
    const hourKey = String(row?.hour_start || row?.hour_end || "hour").trim() || "hour";
    if (!acc[hourKey]) acc[hourKey] = [];
    acc[hourKey].push(row);
    return acc;
  }, {});

  const perHourValues = Object.values(buckets)
    .map((hourRows) => {
      const values = hourRows.map((row) => getNumeric(row?.planned_units)).filter((v) => !Number.isNaN(v));
      if (!values.length) return NaN;

      if (config === "PARALLEL") return values.reduce((sum, value) => sum + value, 0);
      if (config === "SERIES") {
        const targetRow = hourRows.find((row) => {
          const machine = normalizeMachineLabel(row?.machine);
          return machine && (machine === lastSeriesMachine?.id || machine === lastSeriesMachine?.name);
        });
        const targetValue = getNumeric(targetRow?.planned_units);
        if (Number.isFinite(targetValue)) return targetValue;
        return values[values.length - 1];
      }
      return values.reduce((sum, value) => sum + value, 0);
    })
    .filter((value) => Number.isFinite(value));

  if (!perHourValues.length) return null;
  return perHourValues.reduce((sum, value) => sum + value, 0) / perHourValues.length;
};

const normalizeKey = (key) => String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const mapHourlyKey = (key) => {
  const normalized = normalizeKey(key);
  if (normalized === "date") return "date";
  if (normalized === "hourstart" || normalized === "starttime") return "hour_start";
  if (normalized === "hourend" || normalized === "endtime") return "hour_end";
  if (normalized === "machine" || normalized === "machinename") return "machine";
  if (normalized === "process" || normalized === "processname") return "process";
  if (normalized === "plannedunits" || normalized === "planned") return "planned_units";
  if (normalized === "actualunits" || normalized === "actual") return "actual_units";
  if (normalized === "defectstotal" || normalized === "defects") return "defects_total";
  if (normalized === "downtimeminutes" || normalized === "downtime") return "downtime_minutes";
  return key;
};

const mapDailyKey = (key) => {
  const normalized = normalizeKey(key);
  if (normalized === "date") return "date";
  if (normalized === "plannedunits" || normalized === "planned") return "planned_units";
  if (normalized === "actualunits" || normalized === "actual") return "actual_units";
  if (normalized === "defectstotal" || normalized === "defects") return "defects_total";
  if (normalized === "qualityfpyactualpct" || normalized === "fpy" || normalized === "fpyactual") {
    return "quality_fpy_actual_pct";
  }
  return key;
};

const parseHourlyCsv = (text) => {
  if (!text) return [];
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((value) => value.trim());
  return lines.slice(1).map((line) => {
    const values = line.split(",");
    return headers.reduce((acc, header, index) => {
      acc[mapHourlyKey(header)] = values[index] ?? "";
      return acc;
    }, {});
  });
};

const parseDailyCsv = (text) => {
  if (!text) return [];
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((value) => value.trim());
  return lines.slice(1).map((line) => {
    const values = line.split(",");
    return headers.reduce((acc, header, index) => {
      acc[mapDailyKey(header)] = values[index] ?? "";
      return acc;
    }, {});
  });
};

const computeFpyFromDaily = (rows, targetDate, prefetchedRow = null) => {
  if (!Array.isArray(rows) || !rows.length || !targetDate) return null;
  const row = prefetchedRow || rows.find((entry) => parseIsoDate(entry?.date) === targetDate);
  if (!row) return null;

  const directFpy = getNumeric(row?.quality_fpy_actual_pct);
  if (Number.isFinite(directFpy)) return directFpy;

  const actualUnits = getNumeric(row?.actual_units);
  const defectsTotal = getNumeric(row?.defects_total);
  if (Number.isFinite(actualUnits) && actualUnits > 0 && Number.isFinite(defectsTotal)) {
    return ((actualUnits - defectsTotal) / actualUnits) * 100;
  }

  return null;
};

const computeHourlyDowntimeTotal = (rows, targetDate, prefilteredRows = null) => {
  if (!Array.isArray(rows) || !rows.length || !targetDate) return null;
  const rowsOnDate = Array.isArray(prefilteredRows)
    ? prefilteredRows
    : rows.filter((row) => parseIsoDate(row?.date) === targetDate);
  if (!rowsOnDate.length) return null;

  const total = rowsOnDate.reduce((sum, row) => {
    const downtime = getNumeric(row?.downtime_minutes);
    return sum + (Number.isFinite(downtime) ? downtime : 0);
  }, 0);

  return total;
};

const ProcessCard = ({ process, variant = "default", metrics, onClick, showBottleneckCallout = false, bottleneckDetail = "", bottleneckShortDetail = "", onAgentDeepDive }) => {
  const isClickable = typeof onClick === "function";

  return (
    <div
      className={`ff1-process-card ff1-process-card--${variant} ${metrics?.isLowestActual ? "ff1-process-card--bottleneck" : ""} ${isClickable ? "ff1-process-card--clickable" : ""}`}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }
        : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="ff1-process-id">P{process?.process_id ?? "-"}</div>
      <div className="ff1-process-name">{process?.process_name || "Unnamed"}</div>
      <div className="ff1-capacity-wrap" aria-label="PPH">
        <div className="ff1-capacity-head">
          <div className="ff1-capacity-label">PPH</div>
          <div className="ff1-capacity-values">{metrics?.actualLabel || "-"} / {metrics?.goalLabel || "-"}</div>
        </div>
        <div className="ff1-capacity-track">
          <div
            className={`ff1-capacity-fill ${
              metrics?.isLowestActual
                ? "ff1-capacity-fill--bottleneck"
                : metrics?.capacityPct >= 95
                  ? "ff1-capacity-fill--good"
                  : "ff1-capacity-fill--warn"
            }`}
            style={{ width: `${Math.max(0, Math.min(metrics?.capacityPct || 0, 100))}%` }}
          />
        </div>
      </div>
      <div className="ff1-process-bottom">
        <span className="ff1-process-kpi">FPY: {metrics?.fpyLabel || "-"}</span>
        <span className="ff1-process-defects">Downtime: {metrics?.downtimeLabel || "-"}</span>
      </div>
      {showBottleneckCallout && (
        <div className="ff1-bottleneck-callout" role="status" aria-live="polite">
          <div className="ff1-bottleneck-callout-title">⚠ Bottleneck</div>
          <div className="ff1-bottleneck-callout-body">
            <span className="ff1-callout-body--full">{bottleneckDetail || "Lowest actual PPH in selected date range."}</span>
            <span className="ff1-callout-body--short">{bottleneckShortDetail || bottleneckDetail || "Lowest actual PPH."}</span>
          </div>
          {typeof onAgentDeepDive === "function" && (
            <button
              type="button"
              className="ff1-bottleneck-callout-btn"
              onClick={(event) => {
                event.stopPropagation();
                onAgentDeepDive(
                  "Perform bottleneck analysis for my factory's production and compare yesterday's performance with the last 30 days'.",
                );
              }}
            >
              <span className="ff1-callout-btn--full">🤖 Agentic AI Deepdive</span>
              <span className="ff1-callout-btn--short">🤖 Ask Agent</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function FactoryFloor1Table({ getDataSourceConfig, buildBlobUrl, onAgentDeepDive }) {
  const [processes, setProcesses] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [hourlyRowsByProcess, setHourlyRowsByProcess] = useState({});
  const [latestHourlyDate, setLatestHourlyDate] = useState("");
  const [dailyRowsByProcess, setDailyRowsByProcess] = useState({});
  const [latestDailyDate, setLatestDailyDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const diagramGridRef = useRef(null);
  const stagingAnchorRef = useRef(null);
  const upstreamGroupRef = useRef(null);
  const pitBoxRef = useRef(null);
  const seriesBoxRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchLayout = async () => {
      setStatus((prev) => (prev === "ready" ? prev : "loading"));
      setError("");

      try {
        const config = await getDataSourceConfig();
        const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const preferBlob = config?.preferBlob !== false && (!isLocalhost || config?.preferBlobOnLocalhost !== false);
        const configuredFolder = config?.factoryLayoutFolder || config?.layoutFolder || DEFAULT_LAYOUT_FOLDER;
        const fileName = config?.factoryLayoutFile || DEFAULT_LAYOUT_FILE;
        const folderCandidates = [
          configuredFolder,
          DEFAULT_LAYOUT_FOLDER,
          "factory_layout",
        ].filter(Boolean);

        const blobUrls =
          preferBlob && config
            ? folderCandidates.map((folder) => buildBlobUrl(config, folder, fileName)).filter(Boolean)
            : [];

        const candidateUrls = [...blobUrls, "/0_FactoryLayout/factory_layout_standards.json"].filter(Boolean);
        let payload = null;

        for (const url of candidateUrls) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 4000);
            const response = await fetch(url, { cache: "no-store", signal: controller.signal });
            clearTimeout(timeoutId);
            if (!response.ok) continue;
            payload = await response.json();
            break;
          } catch (innerError) {
            // Continue to next source.
          }
        }

        if (!payload) {
          throw new Error("Factory layout file not found in blob or local public folder.");
        }

        if (!isMounted) return;

        const computedProcesses = Array.isArray(payload?.factory?.processes)
          ? [...payload.factory.processes].sort(byProcessOrder)
          : [];
        setProcesses(computedProcesses);
        setStatus("ready");
      } catch (fetchError) {
        if (!isMounted) return;
        setStatus("error");
        setError(fetchError?.message || "Failed to load factory layout.");
      }
    };

    fetchLayout();
    const timer = setInterval(fetchLayout, REFRESH_MS);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [buildBlobUrl, getDataSourceConfig]);

  useEffect(() => {
    let alive = true;

    const fetchHourly = async () => {
      try {
        const config = await getDataSourceConfig();
        const hourlyFolder = config?.hourlyFolder || DEFAULT_HOURLY_FOLDER;
        const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const useBlobForData = config?.preferBlob !== false && (!isLocalhost || config?.preferBlobOnLocalhost !== false);
        const nextRows = {};
        const dates = [];

        const fetchFirstAvailable = async (urls) => {
          for (const url of urls) {
            if (!url) continue;
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 4000);
              const response = await fetch(url, { cache: "no-store", signal: controller.signal });
              clearTimeout(timeoutId);
              if (response.ok) return response.text();
            } catch (_error) {
              // Ignore and try next source.
            }
          }
          return "";
        };

        await Promise.all(
          processes.map(async (process) => {
            const fileName = `process_${process.process_id}.csv`;
            const blobUrl = useBlobForData && config ? buildBlobUrl(config, hourlyFolder, fileName) : null;
            const localUrl = `/data/HourlyData/${fileName}`;
            const text = await fetchFirstAvailable([blobUrl, localUrl]);
            const rows = parseHourlyCsv(text);
            if (rows.length) {
              nextRows[process.process_id] = rows;
              rows.forEach((row) => {
                const d = parseIsoDate(row?.date);
                if (d) dates.push(d);
              });
            }
          }),
        );

        if (!alive) return;
        setHourlyRowsByProcess(nextRows);
        setLatestHourlyDate(dates.sort().at(-1) || "");
      } catch (_error) {
        if (!alive) return;
        setHourlyRowsByProcess({});
        setLatestHourlyDate("");
      }
    };

    if (processes.length) fetchHourly();
    return () => {
      alive = false;
    };
  }, [buildBlobUrl, getDataSourceConfig, processes]);

  useEffect(() => {
    const fallbackDate = latestHourlyDate || latestDailyDate;
    if (!fallbackDate) return;

    if (!startDate) setStartDate(fallbackDate);
    if (!endDate) setEndDate(fallbackDate);
  }, [endDate, latestDailyDate, latestHourlyDate, startDate]);

  const selectedRange = useMemo(() => normalizeDateRange(startDate, endDate), [endDate, startDate]);

  const effectiveRange = useMemo(() => {
    if (selectedRange.start || selectedRange.end) return selectedRange;
    const fallback = latestHourlyDate || latestDailyDate || "";
    if (!fallback) return selectedRange;
    return { start: fallback, end: fallback };
  }, [latestDailyDate, latestHourlyDate, selectedRange]);

  const precomputedHourlyByProcess = useMemo(() => {
    return processes.reduce((acc, process) => {
      const rows = hourlyRowsByProcess[process.process_id] || [];
      const groupedByDate = rows.reduce((dateAcc, row) => {
        const date = parseIsoDate(row?.date);
        if (!date) return dateAcc;
        if (!dateAcc[date]) dateAcc[date] = [];
        dateAcc[date].push(row);
        return dateAcc;
      }, {});

      const perDate = Object.keys(groupedByDate).reduce((dateAcc, date) => {
        const rowsOnDate = groupedByDate[date];
        dateAcc[date] = {
          goal: computePlannedPph(process, rows, date, rowsOnDate),
          actual: computeActualPph(process, rows, date, rowsOnDate),
          downtime: computeHourlyDowntimeTotal(rows, date, rowsOnDate),
        };
        return dateAcc;
      }, {});

      acc[process.process_id] = perDate;
      return acc;
    }, {});
  }, [hourlyRowsByProcess, processes]);

  const precomputedDailyFpyByProcess = useMemo(() => {
    return processes.reduce((acc, process) => {
      const rows = dailyRowsByProcess[process.process_id] || [];
      const perDate = rows.reduce((dateAcc, row) => {
        const date = parseIsoDate(row?.date);
        if (!date) return dateAcc;
        if (dateAcc[date] !== undefined) return dateAcc;
        dateAcc[date] = computeFpyFromDaily(rows, date, row);
        return dateAcc;
      }, {});

      acc[process.process_id] = perDate;
      return acc;
    }, {});
  }, [dailyRowsByProcess, processes]);

  const reportSubtitle = useMemo(() => {
    const hasStart = Boolean(selectedRange.start);
    const hasEnd = Boolean(selectedRange.end);
    if (!hasStart && !hasEnd) return "Windshield v/s Rear View";

    if (hasStart && hasEnd) {
      if (selectedRange.start === selectedRange.end) {
        if (selectedRange.start === YESTERDAY_ALIAS_DATE) {
          return "Windshield v/s Rear View";
        }
        return `How Did We Do on ${formatDisplayDate(selectedRange.start)}?`;
      }
      return `How Did We Do from ${formatDisplayDateWords(selectedRange.start)} to ${formatDisplayDateWords(selectedRange.end)}?`;
    }

    const selectedDate = selectedRange.start || selectedRange.end;
    if (selectedDate === YESTERDAY_ALIAS_DATE) return "Windshield v/s Rear View";
    return `How Did We Do on ${formatDisplayDate(selectedDate)}?`;
  }, [selectedRange.end, selectedRange.start]);

  const reportTitle = useMemo(() => {
    return `Atlanta Plant - ${reportSubtitle}`;
  }, [reportSubtitle]);

  useEffect(() => {
    let alive = true;

    const fetchDaily = async () => {
      try {
        const config = await getDataSourceConfig();
        const dailyFolder = config?.dailyFolder || DEFAULT_DAILY_FOLDER;
        const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const useBlobForData = config?.preferBlob !== false && (!isLocalhost || config?.preferBlobOnLocalhost !== false);
        const nextRows = {};
        const dates = [];

        const fetchFirstAvailable = async (urls) => {
          for (const url of urls) {
            if (!url) continue;
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 4000);
              const response = await fetch(url, { cache: "no-store", signal: controller.signal });
              clearTimeout(timeoutId);
              if (response.ok) return response.text();
            } catch (_error) {
              // Ignore and try next source.
            }
          }
          return "";
        };

        await Promise.all(
          processes.map(async (process) => {
            const fileName = `process_${process.process_id}.csv`;
            const blobUrl = useBlobForData && config ? buildBlobUrl(config, dailyFolder, fileName) : null;
            const localUrl = `/data/${fileName}`;
            const text = await fetchFirstAvailable([blobUrl, localUrl]);
            const rows = parseDailyCsv(text);
            if (rows.length) {
              nextRows[process.process_id] = rows;
              rows.forEach((row) => {
                const d = parseIsoDate(row?.date);
                if (d) dates.push(d);
              });
            }
          }),
        );

        if (!alive) return;
        setDailyRowsByProcess(nextRows);
        setLatestDailyDate(dates.sort().at(-1) || "");
      } catch (_error) {
        if (!alive) return;
        setDailyRowsByProcess({});
        setLatestDailyDate("");
      }
    };

    if (processes.length) fetchDaily();
    return () => {
      alive = false;
    };
  }, [buildBlobUrl, getDataSourceConfig, processes]);

  const diagram = useMemo(() => {
    const staging = processes.find(
      (process) => process?.process_type === "staging_area" || Number(process?.process_id) === 4,
    );

    const upstream = staging
      ? processes.filter((process) => {
          if (process?.process_id === staging.process_id) return false;
          return toArray(process?.feeds_to).includes(staging.process_id);
        })
      : [];

    const downstream = staging
      ? processes.filter((process) => Number(process?.process_order) > Number(staging?.process_order || 0))
      : [];

    return {
      upstream: [...upstream].sort(byProcessOrder),
      staging,
      downstream: [...downstream].sort(byProcessOrder),
    };
  }, [processes]);

  const processMetricsById = useMemo(() => {
    const base = processes.reduce((acc, process) => {
      const hourlyByDate = precomputedHourlyByProcess[process.process_id] || {};
      const dailyFpyByDate = precomputedDailyFpyByProcess[process.process_id] || {};

      const hourlyDatesInRange = Object.keys(hourlyByDate)
        .filter((date) => isDateInRange(date, effectiveRange.start, effectiveRange.end))
        .sort();

      const dailyDatesInRange = Object.keys(dailyFpyByDate)
        .filter((date) => isDateInRange(date, effectiveRange.start, effectiveRange.end))
        .sort();

      const goalFromLayout = computeGoalPph(process);
      const goalFromHourly = averageOf(
        hourlyDatesInRange.map((date) => hourlyByDate[date]?.goal),
      );
      const goal = Number.isFinite(goalFromHourly) ? goalFromHourly : goalFromLayout;

      const actual = averageOf(
        hourlyDatesInRange.map((date) => hourlyByDate[date]?.actual),
      );
      const fpy = averageOf(
        dailyDatesInRange.map((date) => dailyFpyByDate[date]),
      );
      const downtime = averageOf(
        hourlyDatesInRange.map((date) => hourlyByDate[date]?.downtime),
      );
      const capacityPct = Number.isFinite(goal) && goal > 0 && Number.isFinite(actual) ? (actual / goal) * 100 : 0;
      acc[process.process_id] = {
        goalLabel: Number.isFinite(goal) ? String(Math.round(goal)) : "-",
        actualLabel: Number.isFinite(actual) ? String(Math.round(actual)) : "-",
        fpyLabel: Number.isFinite(fpy) ? `${fpy.toFixed(1)}%` : "-",
        downtimeLabel: Number.isFinite(downtime) ? String(Math.round(downtime)) : "-",
        capacityPct,
        goalRaw: Number.isFinite(goal) ? goal : null,
        actualRaw: Number.isFinite(actual) ? actual : null,
      };
      return acc;
    }, {});

    const actualValues = Object.values(base)
      .map((entry) => entry.actualRaw)
      .filter((value) => Number.isFinite(value));
    const minActual = actualValues.length ? Math.min(...actualValues) : null;

    Object.keys(base).forEach((processId) => {
      const entry = base[processId];
      entry.isLowestActual = Number.isFinite(entry.actualRaw) && Number.isFinite(minActual)
        ? entry.actualRaw === minActual
        : false;
    });

    return base;
  }, [effectiveRange.end, effectiveRange.start, precomputedDailyFpyByProcess, precomputedHourlyByProcess, processes]);

  const machineDetailsByProcess = useMemo(() => {
    return processes.reduce((acc, process) => {
      const rows = hourlyRowsByProcess[process.process_id] || [];
      const rowsInRange = rows.filter((row) => {
        const date = parseIsoDate(row?.date);
        return isDateInRange(date, effectiveRange.start, effectiveRange.end);
      });

      const actualByMachine = rowsInRange.reduce((map, row) => {
        const machineKey = normalizeMachineLabel(row?.machine);
        if (!machineKey) return map;
        const actual = getNumeric(row?.actual_units);
        if (!Number.isFinite(actual)) return map;
        if (!map[machineKey]) map[machineKey] = [];
        map[machineKey].push(actual);
        return map;
      }, {});

      const machines = Array.isArray(process?.machines) ? process.machines : [];
      const machineRows = machines.map((machine, index) => {
        const idKey = normalizeMachineLabel(machine?.machine_id);
        const nameKey = normalizeMachineLabel(machine?.machine_name);
        const values = [
          ...(idKey ? actualByMachine[idKey] || [] : []),
          ...(nameKey && nameKey !== idKey ? actualByMachine[nameKey] || [] : []),
        ];
        const actualPph = averageOf(values);
        const standardPph = getNumeric(machine?.standard_pieces_per_hour);
        return {
          key: `${machine?.machine_id || machine?.machine_name || "machine"}-${index}`,
          index: index + 1,
          machineId: machine?.machine_id || "-",
          machineName: machine?.machine_name || machine?.machine_id || "Unnamed machine",
          actualPphRaw: Number.isFinite(actualPph) ? actualPph : null,
          actualPphLabel: Number.isFinite(actualPph) ? String(Math.round(actualPph)) : "-",
          standardPphLabel: Number.isFinite(standardPph) ? String(Math.round(standardPph)) : "-",
        };
      });

      const minActual = machineRows
        .map((machine) => machine.actualPphRaw)
        .filter((value) => Number.isFinite(value))
        .reduce((min, value) => (min === null || value < min ? value : min), null);

      acc[process.process_id] = machineRows.map((machine) => ({
        ...machine,
        isBottleneck: Number.isFinite(machine.actualPphRaw) && Number.isFinite(minActual)
          ? machine.actualPphRaw === minActual
          : false,
      }));

      return acc;
    }, {});
  }, [effectiveRange.end, effectiveRange.start, hourlyRowsByProcess, processes]);

  const selectedProcess = useMemo(
    () => processes.find((process) => process.process_id === selectedProcessId) || null,
    [processes, selectedProcessId],
  );

  const selectedMachineDetails = useMemo(
    () => (selectedProcess ? machineDetailsByProcess[selectedProcess.process_id] || [] : []),
    [machineDetailsByProcess, selectedProcess],
  );

  const selectedProcessMetrics = useMemo(
    () => (selectedProcess ? processMetricsById[selectedProcess.process_id] || null : null),
    [processMetricsById, selectedProcess],
  );

  const bottleneckSummary = useMemo(() => {
    const candidate = processes
      .map((process) => ({
        process,
        metrics: processMetricsById[process.process_id] || null,
      }))
      .find((entry) => entry.metrics?.isLowestActual);

    if (!candidate) return null;

    const processName = candidate.process?.process_name || `P${candidate.process?.process_id || "-"}`;
    const goal = candidate.metrics?.goalRaw;
    const actual = candidate.metrics?.actualRaw;

    if (!Number.isFinite(goal) || !Number.isFinite(actual) || goal <= 0) {
      return {
        processName,
        detail: "Lowest actual PPH in selected date range.",
      };
    }

    const gap = Math.max(0, Math.round(goal - actual));
    const pct = Math.max(0, Math.round(((goal - actual) / goal) * 100));
    return {
      processId: candidate.process?.process_id,
      processName,
      detail: `${gap} PPH (${pct}%) below target.`,
      shortDetail: `${pct}% below target.`,
    };
  }, [processMetricsById, processes]);

  const selectedSidebarHeaderClass = useMemo(() => {
    if (!selectedProcessMetrics) return "ff1-sidebar-header--warn";
    if (selectedProcessMetrics.isLowestActual) return "ff1-sidebar-header--bottleneck";
    return selectedProcessMetrics.capacityPct >= 95
      ? "ff1-sidebar-header--good"
      : "ff1-sidebar-header--warn";
  }, [selectedProcessMetrics]);

  return (
    <div className="card">
      <div className="report-header">
        <div>
          <h2 className="report-title">{reportTitle}</h2>
        </div>
        <div className="ff1-header-actions">
          <button
            type="button"
            className="ff1-powered-btn"
            aria-label="Powered by StratAlign AI"
            onClick={() => {
              window.location.href = "https://www.stratalignsolution.com/";
            }}
          >
            Powered by StratAlign AI
          </button>
        </div>
      </div>

      <div className="report-controls ff1-date-controls">
        <label className="report-control">
          Start date
          <input
            type="date"
            className="report-input"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>
        <label className="report-control">
          End date
          <input
            type="date"
            className="report-input"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
      </div>

      {status === "loading" && <p className="report-status">Loading factory layout…</p>}
      {status === "error" && <p className="report-status report-error">{error}</p>}

      {status === "ready" && (
        <div className="ff1-diagram-wrap">
          <div className="ff1-diagram-grid" ref={diagramGridRef}>
            {/* Removed arrows between Parallel, PIT, and Series */}

            <div className="ff1-left-column">
              <div className="ff1-upstream-box" ref={upstreamGroupRef}>
                <div className="ff1-upstream-box-label">Parallel</div>
                <div className="ff1-lane ff1-lane--parallel ff1-lane--parallel-vertical">
                  {diagram.upstream.map((process) => (
                    <div key={`upstream-${process.process_id}`} className="ff1-upstream-row">
                      <div className={`ff1-upstream-anchor${bottleneckSummary?.processId === process.process_id ? ' ff1-upstream-anchor--bottleneck' : ''}`}>
                        <ProcessCard
                          process={process}
                          variant="upstream"
                          metrics={processMetricsById[process.process_id]}
                          showBottleneckCallout={bottleneckSummary?.processId === process.process_id}
                          bottleneckDetail={bottleneckSummary?.detail}
                          bottleneckShortDetail={bottleneckSummary?.shortDetail}
                          onAgentDeepDive={onAgentDeepDive}
                          onClick={() => setSelectedProcessId(process.process_id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {diagram.staging && (
              <div className="ff1-middle-column">
                <div className="ff1-pit-box" ref={pitBoxRef}>
                  <div className="ff1-pit-box-label">PIT</div>
                  <div className="ff1-staging-anchor" ref={stagingAnchorRef}>
                    <ProcessCard
                      process={diagram.staging}
                      variant="staging"
                      metrics={processMetricsById[diagram.staging?.process_id]}
                      showBottleneckCallout={bottleneckSummary?.processId === diagram.staging?.process_id}
                      bottleneckDetail={bottleneckSummary?.detail}
                      bottleneckShortDetail={bottleneckSummary?.shortDetail}
                      onAgentDeepDive={onAgentDeepDive}
                      onClick={() => setSelectedProcessId(diagram.staging?.process_id)}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="ff1-right-column">
              {diagram.staging && (
                <>
                  <div className="ff1-series-box" ref={seriesBoxRef}>
                    <div className="ff1-series-box-label">Series</div>
                    <div className="ff1-lane ff1-lane--main">
                      {diagram.downstream
                        .filter((process) => process.process_id === 5 || process.process_id === 6 || process.process_id === 7)
                        .map((process) => (
                          <div
                            key={`downstream-${process.process_id}`}
                            className="ff1-downstream-anchor"
                          >
                            <ProcessCard
                              process={process}
                              variant="downstream"
                              metrics={processMetricsById[process.process_id]}
                              showBottleneckCallout={bottleneckSummary?.processId === process.process_id}
                              bottleneckDetail={bottleneckSummary?.detail}
                              bottleneckShortDetail={bottleneckSummary?.shortDetail}
                              onAgentDeepDive={onAgentDeepDive}
                              onClick={() => setSelectedProcessId(process.process_id)}
                            />
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Removed extra downstream anchors outside Series box */}
                </>
              )}
            </div>
          </div>{/* end ff1-diagram-grid */}

          {/* Legend — absolute bottom-center of wrap */}
          <div className="ff1-legend" aria-label="Flow chart color legend">
            <div className="ff1-legend-title">Legend</div>
            <div className="ff1-legend-item">
              <span className="ff1-legend-swatch ff1-legend-swatch--good" />
              <span>PPH ≥ 95%</span>
            </div>
            <div className="ff1-legend-item">
              <span className="ff1-legend-swatch ff1-legend-swatch--warn" />
              <span>PPH &lt; 95%</span>
            </div>
            <div className="ff1-legend-item">
              <span className="ff1-legend-swatch ff1-legend-swatch--bottleneck" />
              <span>Bottleneck process</span>
            </div>
          </div>

          <div className="ff1-pred-column">
            <PredictiveAlertPanel onAgentDeepDive={onAgentDeepDive} />
          </div>

          {!diagram.staging && !!processes.length && (
            <div className="ff1-lane ff1-lane--main">
              {processes.map((process, index) => (
                <React.Fragment key={`fallback-${process.process_id}`}>
                  <ProcessCard
                    process={process}
                    variant="downstream"
                    metrics={processMetricsById[process.process_id]}
                    showBottleneckCallout={bottleneckSummary?.processId === process.process_id}
                    bottleneckDetail={bottleneckSummary?.detail}
                    bottleneckShortDetail={bottleneckSummary?.shortDetail}
                    onAgentDeepDive={onAgentDeepDive}
                    onClick={() => setSelectedProcessId(process.process_id)}
                  />
                  {index < processes.length - 1 && <div className="ff1-arrow" aria-hidden="true" />}
                </React.Fragment>
              ))}
            </div>
          )}

          {!processes.length && (
            <div className="report-chart-empty">No process layout found in factory layout JSON.</div>
          )}

          {selectedProcess && (
            <>
              <button
                type="button"
                className="ff1-sidebar-backdrop"
                aria-label="Close machine details"
                onClick={() => setSelectedProcessId(null)}
              />
              <aside className="ff1-sidebar" aria-label="Machine PPH details">
                <div className={`ff1-sidebar-header ${selectedSidebarHeaderClass}`}>
                  <div className="ff1-sidebar-header-content">
                    <div className="ff1-sidebar-title">{selectedProcess?.process_name || "Value stream"}</div>
                    <div className="ff1-sidebar-subtitle">Machine PPH details</div>
                  </div>
                  <button
                    type="button"
                    className="ff1-sidebar-close"
                    aria-label="Close"
                    onClick={() => setSelectedProcessId(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="ff1-sidebar-metrics">
                  <div className="ff1-sidebar-metric">
                    <div className="ff1-sidebar-metric-label">Throughput</div>
                    <div className="ff1-sidebar-metric-value">{selectedProcessMetrics?.actualLabel || "-"} <span>PPH</span></div>
                  </div>
                  <div className="ff1-sidebar-metric ff1-sidebar-metric--right">
                    <div className="ff1-sidebar-metric-label">Configuration</div>
                    <div className="ff1-sidebar-metric-mode">
                      {String(selectedProcess?.machine_configuration || "-").toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className="ff1-sidebar-list">
                  {selectedMachineDetails.length ? (
                    selectedMachineDetails.map((machine) => (
                      <div className={`ff1-sidebar-item ${machine.isBottleneck ? "ff1-sidebar-item--bottleneck" : ""}`} key={machine.key}>
                        <div className={`ff1-sidebar-item-index ${machine.isBottleneck ? "ff1-sidebar-item-index--bottleneck" : ""}`}>{machine.index}</div>
                        <div className="ff1-sidebar-item-main">
                          <div className="ff1-sidebar-item-name">{machine.machineName}</div>
                          <div className="ff1-sidebar-item-meta">{machine.machineId}</div>
                        </div>
                        <div className="ff1-sidebar-item-right">
                          <div className={`ff1-sidebar-item-pph ${machine.isBottleneck ? "ff1-sidebar-item-pph--bottleneck" : ""}`}>{machine.actualPphLabel} <span>PPH</span></div>
                          <div className="ff1-sidebar-item-standard">{machine.standardPphLabel}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="report-chart-empty">No machine data for this process in selected date range.</div>
                  )}
                </div>
              </aside>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Predictive Alert Panel ──────────────────────────────────────────────────
// Rendered inline as the 4th column of ff1-diagram-grid, at the same horizontal
// level as the PARALLEL bubble. Expands downward when clicked; still exported
// so it can be unit-tested or reused independently.

const PRED_COLORS = {
  HIGH:   { bg: "rgba(220,38,38,0.09)",  border: "rgba(220,38,38,0.55)",  text: "#991b1b", dot: "#dc2626" },
  MEDIUM: { bg: "rgba(251,191,36,0.10)", border: "rgba(251,146,60,0.55)", text: "#92400e", dot: "#d97706" },
  LOW:    { bg: "rgba(16,185,129,0.09)", border: "rgba(52,211,153,0.55)", text: "#065f46", dot: "#10b981" },
};

const SEVERITY_ICON = { HIGH: "🔴", MEDIUM: "🟠", LOW: "🟡" };

function getPredictionsUrl() {
  // The Foundry agent calls blobaccessapi/api/predictions (server-to-server, no CORS).
  // The browser panel must call the agent function app directly — blobaccessapi does NOT
  // include the SWA origin in its CORS allowed-origins list, so browser fetches must go
  // to the correct environment's function app. Derive from REACT_APP_AGENT_API_URL so
  // PPE builds hit PPE API and PROD builds hit PROD API automatically.
  //
  // PPE:  REACT_APP_AGENT_API_URL=https://stratalign-factory-api-ppe.azurewebsites.net/api/agent
  // PROD: REACT_APP_AGENT_API_URL=https://stratalign-factory-api.azurewebsites.net/api/agent
  if (process.env.NODE_ENV === "production") {
    const base = (process.env.REACT_APP_AGENT_API_URL || "https://stratalign-factory-api-ppe.azurewebsites.net/api/agent")
      .replace(/\/agent$/, "");
    return `${base}/predictions?limit=100`;
  }
  // Local dev: setupProxy.js forwards /api/* to localhost:7071
  return "/api/predictions?limit=100";
}

export function PredictiveAlertPanel({ onAgentDeepDive }) {
  const [alerts, setAlerts]     = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState({ today: false, tomorrow: false });
  const [loading, setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [expandedMaxHeight, setExpandedMaxHeight] = useState(null);
  const panelRef = useRef(null);

  // Collapse on click-outside
  useEffect(() => {
    if (!expanded) return;
    function handleMouseDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setExpanded(false);
        setShowMore({ today: false, tomorrow: false });
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [expanded]);

  useEffect(() => {
    let cancelled = false;
    const url = getPredictionsUrl();

    fetch(url, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!cancelled) {
          setAlerts(data.alerts || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetchError(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!expanded) {
      setExpandedMaxHeight(null);
      return;
    }

    const recalcMaxHeight = () => {
      if (typeof window === "undefined" || window.innerWidth <= 1100) {
        setExpandedMaxHeight(null);
        return;
      }

      const panelEl = panelRef.current;
      if (!panelEl) {
        setExpandedMaxHeight(null);
        return;
      }

      const wrapEl = panelEl.closest(".ff1-diagram-wrap");
      if (!wrapEl) {
        setExpandedMaxHeight(null);
        return;
      }

      const wrapRect = wrapEl.getBoundingClientRect();
      const panelRect = panelEl.getBoundingClientRect();
      const bottomPadding = 16;
      const available = Math.floor(wrapRect.bottom - panelRect.top - bottomPadding);

      if (Number.isFinite(available) && available > 220) {
        setExpandedMaxHeight(available);
      } else {
        setExpandedMaxHeight(null);
      }
    };

    recalcMaxHeight();
    window.addEventListener("resize", recalcMaxHeight);
    return () => window.removeEventListener("resize", recalcMaxHeight);
  }, [expanded, showMore.today, showMore.tomorrow, alerts.length]);

  // Don't render anything while loading, on error, or if no actionable alerts
  if (loading || fetchError || !alerts.length) return null;

  // Only count alerts the panel actually displays (today = next_6h, tomorrow = next_24h)
  const displayedAlerts = alerts.filter((a) => a.horizon === "next_6h" || a.horizon === "next_24h");
  const hasHigh = displayedAlerts.some((a) => a.severity === "HIGH");
  const buttonColor = hasHigh ? PRED_COLORS.HIGH : PRED_COLORS.MEDIUM;

  return (
    <div
      ref={panelRef}
      className={`ff1-pred-box${expanded ? " ff1-pred-box--expanded" : ""}`}
      style={expandedMaxHeight ? { maxHeight: `${expandedMaxHeight}px` } : undefined}
    >
      {/* — Outer label, like ff1-series-box-label — */}
      <div className="ff1-pred-box-label">Predictive Alerting</div>

      {/* — Inner card (bubble-within-bubble), like a ProcessCard inside SERIES — */}
      <button
        className="ff1-pred-inner-card"
        onClick={() => setExpanded((v) => !v)}
        style={{
          borderColor: buttonColor.border,
          background: buttonColor.bg,
          color: buttonColor.text,
        }}
      >
        <span className="ff1-pred-inner-card-icon">{hasHigh ? "🔴" : "🟡"}</span>
        <span className="ff1-pred-inner-card-text">
          <span className="ff1-pred-inner-card-count">{displayedAlerts.length} alerts</span>
          <span className="ff1-pred-inner-card-sev">{hasHigh ? "HIGH priority" : "MEDIUM priority"}</span>
        </span>
        <span className="ff1-pred-inner-card-chevron">{expanded ? "▲" : "▼"}</span>
      </button>

      {/* — Expanded list inside the outer bubble — */}
      {expanded && (() => {
        const todayAlerts    = alerts.filter((a) => a.horizon === "next_6h");
        const todayFirst3    = todayAlerts.slice(0, 3);
        const todayExtra     = todayAlerts.slice(3);
        const tomorrowAlerts = alerts.filter((a) => a.horizon === "next_24h");

        const renderAlertCard = (alert, idx, bgAlpha, isLast) => {
          const c = PRED_COLORS[alert.severity] || PRED_COLORS.MEDIUM;
          return (
            <div
              key={alert.id || `card-${idx}`}
              style={{
                padding: "10px 14px 9px",
                borderTop: idx === 0 && bgAlpha === "transparent" ? "none" : "1px solid rgba(148,163,184,0.18)",
                background: bgAlpha,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.72rem" }}>{SEVERITY_ICON[alert.severity] || "🟡"}</span>
                {alert.processName && (
                  <span style={{ fontWeight: 700, color: c.text, fontSize: "0.68rem", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 3, padding: "1px 5px" }}>
                    {alert.processName}{alert.machine ? ` · ${alert.machine}` : ""}
                  </span>
                )}
                <span style={{ color: "#94a3b8", fontSize: "0.65rem", marginLeft: 2 }}>{alert.horizonLabel || alert.horizon}</span>
                <span style={{ marginLeft: "auto", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 4, padding: "1px 6px", color: c.text, fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.05em" }}>
                  {alert.severity}
                </span>
              </div>
              {alert.outcomeLabel && (
                <div style={{ color: "#1e293b", fontSize: "0.76rem", lineHeight: 1.45, marginBottom: 6 }}>
                  {alert.outcomeLabel}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                <span style={{ color: "#94a3b8", fontSize: "0.63rem", fontStyle: "italic" }}>{alert.signalName}</span>
                {typeof onAgentDeepDive === "function" && alert.agentPrompt && (
                  <button
                    onClick={() => { setExpanded(false); onAgentDeepDive(alert.agentPrompt); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 10px", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 5, color: c.text, fontFamily: "inherit", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.03em", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, transition: "background 0.12s" }}
                    onMouseOver={(e) => { e.currentTarget.style.background = hasHigh ? "rgba(220,38,38,0.18)" : "rgba(251,191,36,0.20)"; }}
                    onMouseOut={(e)  => { e.currentTarget.style.background = c.bg; }}
                  >
                    🤖 Analyze →
                  </button>
                )}
              </div>
            </div>
          );
        };

        return (
          <div className="ff1-pred-list">

            {/* ── TODAY section ── */}
            <div className="ff1-pred-today-hdr">📅 Today</div>
            <div className="ff1-pred-list-scroll">
              {todayFirst3.map((alert, idx) => renderAlertCard(alert, idx, "transparent", false))}

              {/* "X more today" — red toggle, stays inside today scroll */}
              {todayExtra.length > 0 && (
                <>
                  <button
                    className="ff1-pred-more-today"
                    onClick={() => setShowMore((v) => ({ ...v, today: !v.today }))}
                    onMouseOver={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.12)"; }}
                    onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(220,38,38,0.06)"; }}
                  >
                    <span>{showMore.today ? "▾" : "▸"}</span>
                    <span>{showMore.today ? "▾ Hide extra" : `${todayExtra.length} more today…`}</span>
                  </button>
                  {showMore.today && todayExtra.map((a, i) => renderAlertCard(a, i, "rgba(254,242,242,0.45)", false))}
                </>
              )}
            </div>{/* end today scroll */}

            {/* ── TOMORROW section — pinned below today scroll, always visible ── */}
            {tomorrowAlerts.length > 0 && (
              <>
                <button
                  className="ff1-pred-tomorrow-hdr"
                  onClick={() => setShowMore((v) => ({ ...v, tomorrow: !v.tomorrow }))}
                  onMouseOver={(e) => { e.currentTarget.style.background = "rgba(224,231,255,0.55)"; }}
                  onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(224,231,255,0.28)"; }}
                >
                  <span className="ff1-pred-tomorrow-chevron">{showMore.tomorrow ? "▾" : "▸"}</span>
                  <span className="ff1-pred-tomorrow-label">📋 Plan for Tomorrow</span>
                  <span className="ff1-pred-tomorrow-badge">{tomorrowAlerts.length}</span>
                </button>
                {showMore.tomorrow && (
                  <div className="ff1-pred-tomorrow-scroll">
                    {tomorrowAlerts.map((a, i) => renderAlertCard(a, i, "rgba(238,242,255,0.45)", i === tomorrowAlerts.length - 1))}
                  </div>
                )}
              </>
            )}

            {/* Footer — always pinned at bottom */}
            <div className="ff1-pred-footer">
              Powered by StratAlign Predictive Analytics · refreshes hourly
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default FactoryFloor1Table;
